import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Switch } from "@/components/ui/switch";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { supabase } from "@/integrations/supabase/client";
import {
  readConsent,
  writeConsent,
  getOrCreateSessionId,
  type ConsentChoices,
} from "@/lib/consent";

// Bottom-fixed cookie consent banner. Renders only on first visit
// (no `ct_cookie_consent` cookie). Records every choice in
// `cookie_consents` so admins can audit and prove compliance.
export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) setVisible(true);
    const handler = () => {
      const c = readConsent();
      if (!c) setVisible(true);
    };
    window.addEventListener("ct:consent-reset", handler);
    return () => window.removeEventListener("ct:consent-reset", handler);
  }, []);

  async function persist(choices: ConsentChoices) {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id ?? null;
      await supabase.from("cookie_consents").insert({
        session_id: getOrCreateSessionId(),
        user_id: userId,
        consent_essential: true,
        consent_analytics: choices.analytics,
        consent_marketing: choices.marketing,
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    } catch (err) {
      // Non-blocking — banner UX should not depend on network.
      console.warn("[cookie-consent] failed to persist", err);
    }
  }

  function handleAcceptAll() {
    const c = writeConsent({ analytics: true, marketing: true });
    void persist(c);
    setVisible(false);
  }

  function handleSavePrefs() {
    const c = writeConsent({ analytics, marketing });
    void persist(c);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      className="fixed inset-x-0 bottom-0 z-[60] border-t-2 border-navy-800 bg-cream-50 shadow-[0_-8px_30px_rgba(168,139,92,0.18)]"
    >
      <div className="mx-auto grid max-w-7xl gap-8 px-6 py-6 lg:grid-cols-[7fr_3fr] lg:items-center">
        {/* Body + toggles */}
        <div>
          <p className="font-sans text-[15px] leading-relaxed text-charcoal-900">
            We use cookies to enhance your experience and analyze platform
            use. Essential cookies are always on. Choose what else you allow:
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-3">
            <ConsentToggle
              label="Essential"
              checked
              locked
              onChange={() => {}}
            />
            <ConsentToggle
              label="Analytics"
              checked={analytics}
              onChange={setAnalytics}
            />
            <ConsentToggle
              label="Marketing"
              checked={marketing}
              onChange={setMarketing}
            />
          </div>
          <Link
            to="/privacy"
            className="mt-3 inline-block font-sans text-xs uppercase tracking-[0.18em] text-bronze-600 hover:text-navy-800"
          >
            Read Privacy Policy →
          </Link>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 lg:items-end">
          <PrimaryButton className="w-full lg:w-auto" onClick={handleAcceptAll}>
            Accept All
          </PrimaryButton>
          <SecondaryButton
            className="w-full border-navy-800 !text-navy-800 hover:!bg-navy-800 hover:!text-cream-50 lg:w-auto"
            onClick={handleSavePrefs}
          >
            Save Preferences
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
}

function ConsentToggle({
  label,
  checked,
  onChange,
  locked = false,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  locked?: boolean;
}) {
  return (
    <label className="flex items-center gap-3">
      <Switch
        checked={checked}
        disabled={locked}
        onCheckedChange={onChange}
        className={locked ? "data-[state=checked]:bg-bronze-600" : ""}
      />
      <span
        className={
          "font-sans text-[11px] font-semibold uppercase tracking-[0.18em] " +
          (locked ? "text-bronze-600" : "text-charcoal-900")
        }
      >
        {label}
        {locked && <span className="ml-1 text-charcoal-500">(always on)</span>}
      </span>
    </label>
  );
}

export default CookieConsentBanner;